import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { AlertTriangle, Loader2, Upload, CheckCircle2, Edit3 } from 'lucide-react';

const PreviewStep = ({
  previewData, selectedResources, editingIdx, setEditingIdx,
  sdohCategories, loading,
  onToggleSelection, onSelectAll, onDeselectAll, onUpdateResource,
  onBack, onExecuteImport
}) => {
  const getWarningsForRow = (idx) => previewData?.warnings.filter(w => w.row === idx + 1) || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {previewData.warnings.length > 0 && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            AI found {previewData.warnings.length} potential issues. Review flagged items below.
          </AlertDescription>
        </Alert>
      )}

      <Card className="rounded-2xl border-[#E0D6C8]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-['Manrope']">Review Resources</CardTitle>
              <CardDescription>
                {selectedResources.length} of {previewData.total} resources selected for import
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onSelectAll}>Select All</Button>
              <Button variant="ghost" size="sm" onClick={onDeselectAll}>Deselect All</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-3">
              {previewData.resources.map((resource, idx) => {
                const warnings = getWarningsForRow(idx);
                const isSelected = selectedResources.includes(idx);
                const isEditing = editingIdx === idx;
                
                return (
                  <div 
                    key={resource.id || `import-${idx}`}
                    className={`p-4 rounded-xl border transition-colors ${
                      isSelected ? 'bg-white border-[#5C4023]/30' : 'bg-[#F3EFE9]/30 border-transparent'
                    } ${warnings.length > 0 ? 'ring-2 ring-amber-200' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox checked={isSelected} onCheckedChange={() => onToggleSelection(idx)} data-testid={`select-resource-${idx}`} />
                      <div className="flex-1 min-w-0">
                        {!isEditing ? (
                          <ResourceReadView resource={resource} />
                        ) : (
                          <ResourceEditView resource={resource} idx={idx} sdohCategories={sdohCategories} onUpdateResource={onUpdateResource} />
                        )}
                        {warnings.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {warnings.map((w, wIdx) => (
                              <div key={`warn-${idx}-${wIdx}`} className="flex items-center gap-1 text-xs text-amber-600">
                                <AlertTriangle className="w-3 h-3" /> {w.message}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setEditingIdx(isEditing ? null : idx)} data-testid={`edit-resource-btn-${idx}`} className="shrink-0">
                        {isEditing ? <CheckCircle2 className="w-4 h-4 text-[#5C4023]" /> : <Edit3 className="w-4 h-4 text-[#888]" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={onBack} className="flex-1 rounded-full">Back</Button>
            <Button onClick={onExecuteImport} disabled={loading || selectedResources.length === 0} data-testid="import-btn" className="flex-1 bg-[#5C4023] hover:bg-[#4A331C] rounded-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import {selectedResources.length} Resources
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ResourceReadView = ({ resource }) => (
  <>
    <div className="flex items-center gap-2 mb-1 flex-wrap">
      <p className="font-medium text-[#1A1A1A] truncate">{resource.resource_name || '(No name)'}</p>
      {resource.primary_sdoh_category && <Badge className="bg-[#F3EFE9] text-[#5C4023] text-xs">{resource.primary_sdoh_category}</Badge>}
      {resource.cost_to_client === 'Free' && <Badge className="bg-emerald-100 text-emerald-800 text-xs">Free</Badge>}
      {resource.neighborhoods_served?.length > 0 && <Badge className="bg-blue-50 text-blue-700 text-xs">{resource.neighborhoods_served[0]}</Badge>}
    </div>
    <p className="text-sm text-[#5A5A5A] truncate">{resource.organization || '(No organization)'}</p>
    <div className="flex flex-wrap gap-3 text-xs text-[#888888] mt-1">
      {resource.phone_number && <span>{resource.phone_number}</span>}
      {resource.physical_address && <span className="truncate max-w-[200px]">{resource.physical_address}</span>}
      {resource.hours && <span className="truncate max-w-[200px]">{resource.hours}</span>}
    </div>
    {resource.client_facing_description && <p className="text-xs text-[#888888] mt-1 line-clamp-1">{resource.client_facing_description}</p>}
  </>
);

const ResourceEditView = ({ resource, idx, sdohCategories, onUpdateResource }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-xs text-[#888]">Resource Name</Label>
        <Input data-testid={`edit-name-${idx}`} value={resource.resource_name || ''} onChange={e => onUpdateResource(idx, 'resource_name', e.target.value)} className="h-8 text-sm rounded-lg" />
      </div>
      <div>
        <Label className="text-xs text-[#888]">Organization</Label>
        <Input data-testid={`edit-org-${idx}`} value={resource.organization || ''} onChange={e => onUpdateResource(idx, 'organization', e.target.value)} className="h-8 text-sm rounded-lg" />
      </div>
      <div>
        <Label className="text-xs text-[#888]">SDOH Category</Label>
        <Select value={resource.primary_sdoh_category || ''} onValueChange={v => onUpdateResource(idx, 'primary_sdoh_category', v)}>
          <SelectTrigger data-testid={`edit-sdoh-${idx}`} className="h-8 text-sm rounded-lg"><SelectValue placeholder="Select category" /></SelectTrigger>
          <SelectContent>{sdohCategories.map(cat => (<SelectItem key={cat} value={cat}>{cat}</SelectItem>))}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-[#888]">Cost</Label>
        <Select value={resource.cost_to_client || 'Free'} onValueChange={v => onUpdateResource(idx, 'cost_to_client', v)}>
          <SelectTrigger data-testid={`edit-cost-${idx}`} className="h-8 text-sm rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Free">Free</SelectItem>
            <SelectItem value="Sliding scale">Sliding scale</SelectItem>
            <SelectItem value="Insurance required">Insurance required</SelectItem>
            <SelectItem value="Fee-based">Fee-based</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-[#888]">Phone</Label>
        <Input data-testid={`edit-phone-${idx}`} value={resource.phone_number || ''} onChange={e => onUpdateResource(idx, 'phone_number', e.target.value)} className="h-8 text-sm rounded-lg" />
      </div>
      <div>
        <Label className="text-xs text-[#888]">Address</Label>
        <Input data-testid={`edit-address-${idx}`} value={resource.physical_address || ''} onChange={e => onUpdateResource(idx, 'physical_address', e.target.value)} className="h-8 text-sm rounded-lg" />
      </div>
      <div>
        <Label className="text-xs text-[#888]">Hours</Label>
        <Input data-testid={`edit-hours-${idx}`} value={resource.hours || ''} onChange={e => onUpdateResource(idx, 'hours', e.target.value)} className="h-8 text-sm rounded-lg" />
      </div>
      <div>
        <Label className="text-xs text-[#888]">Website</Label>
        <Input data-testid={`edit-website-${idx}`} value={resource.website || ''} onChange={e => onUpdateResource(idx, 'website', e.target.value)} className="h-8 text-sm rounded-lg" />
      </div>
    </div>
    <div>
      <Label className="text-xs text-[#888]">Description</Label>
      <textarea
        data-testid={`edit-desc-${idx}`}
        value={resource.client_facing_description || ''}
        onChange={e => onUpdateResource(idx, 'client_facing_description', e.target.value)}
        className="w-full text-sm rounded-lg border border-input bg-background px-3 py-2 min-h-[60px] resize-y"
        rows={2}
      />
    </div>
  </div>
);

export default PreviewStep;
